/**
 * Wirtschaftlichkeit — Steuerungssicht mit Aufriss (Etappe 4, Entscheidung 12.08.)
 *
 * Drei Ebenen in EINER Seite, verbunden über Brotkrumen statt getrennter Reiter:
 *   Gesamt → Team (?team=…) → Mitarbeiter (EmployeeDetailDrawer)
 *
 * Die frühere Seite „Projektumsatz" ist hier aufgegangen — der Monatsverlauf
 * unten ist ihr Inhalt. Damit gibt es nur noch EINE Umsatz-Wahrheit.
 * Der Aufriss steht in der URL (?team=), damit der Stand teilbar ist und der
 * Zurück-Knopf des Browsers eine Ebene zurückspringt statt die Seite zu verlassen.
 */

import { useQuery } from "@tanstack/react-query";
import {
  AlertCircle,
  ChevronRight,
  Database,
  RefreshCw,
  Search,
  TriangleAlert,
  X,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { EmployeeDetailDrawer } from "../components/EmployeeDetailDrawer.js";
import { AppLayout } from "../components/layout/AppLayout.js";
import { Header } from "../components/layout/Header.js";
import { KpiInfo } from "../components/ui/KpiInfo.js";
import { SortKopf } from "../components/ui/SortKopf.js";
import { useTabellenSortierung } from "../hooks/useTabellenSortierung.js";
import {
  type MeinTeamRow,
  type WirtschaftAmpelLevel,
  type WirtschaftGruppe,
  wirtschaftApi,
} from "../lib/api.js";
import type { SortSpalten } from "../lib/tabellen-sortierung.js";
import { nachWirtschaftsAmpel } from "../lib/team-reihenfolge.js";

// ── Formatierung ──────────────────────────────────────────────────────────────

const fmtEur = new Intl.NumberFormat("de-DE", {
  style: "currency",
  currency: "EUR",
  maximumFractionDigits: 0,
});

/** Achsenbeschriftung: gekürzte €-Werte, damit die Achse schmal bleibt. */
function fmtEurAxis(v: number): string {
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `${(v / 1_000_000).toLocaleString("de-DE")} Mio`;
  if (abs >= 1_000) return `${Math.round(v / 1_000).toLocaleString("de-DE")} k`;
  return String(v);
}

/** Prozentwerte kommen ganzzahlig aus der API; null bleibt „—", nie 0. */
function fmtPct(v: number | null): string {
  return v != null ? `${v} %` : "—";
}

const MONTH_LONG: Record<string, string> = {
  "01": "Januar",
  "02": "Februar",
  "03": "März",
  "04": "April",
  "05": "Mai",
  "06": "Juni",
  "07": "Juli",
  "08": "August",
  "09": "September",
  "10": "Oktober",
  "11": "November",
  "12": "Dezember",
};

const MONTH_SHORT: Record<string, string> = {
  "01": "Jan",
  "02": "Feb",
  "03": "Mär",
  "04": "Apr",
  "05": "Mai",
  "06": "Jun",
  "07": "Jul",
  "08": "Aug",
  "09": "Sep",
  "10": "Okt",
  "11": "Nov",
  "12": "Dez",
};

/** "2026-07" → "Juli 2026" */
function fmtMonthLong(ym: string): string {
  const [year, mm] = ym.split("-");
  return `${MONTH_LONG[mm] ?? mm} ${year}`;
}

/** "2026-07" → "Jul 26" (Achsenbeschriftung) */
function fmtMonthShort(ym: string): string {
  const [year, mm] = ym.split("-");
  return `${MONTH_SHORT[mm] ?? mm} ${year.slice(2)}`;
}

// ── Ampeln ────────────────────────────────────────────────────────────────────

const LEVEL_COLOR: Record<WirtschaftAmpelLevel, string> = {
  ok: "var(--risk-low)",
  warning: "var(--risk-medium)",
  critical: "var(--risk-high)",
};

/** Farbiger TEXT nutzt die kontrastgeprüften -text-Varianten (≥ 4.5:1). */
const LEVEL_TEXT_COLOR: Record<WirtschaftAmpelLevel, string> = {
  ok: "var(--risk-low-text)",
  warning: "var(--risk-medium-text)",
  critical: "var(--risk-high-text)",
};

const LEVEL_LABEL: Record<WirtschaftAmpelLevel, string> = {
  ok: "OK",
  warning: "Warnung",
  critical: "Kritisch",
};

const LEVEL_RANK: Record<WirtschaftAmpelLevel, number> = { ok: 0, warning: 1, critical: 2 };

const AMPEL_ORDER: ReadonlyArray<WirtschaftAmpelLevel> = ["ok", "warning", "critical"];

type Ampeln = WirtschaftGruppe["ampeln"];

function ampelnTitle(a: Ampeln): string {
  return AMPEL_ORDER.map((lvl) => `${LEVEL_LABEL[lvl]}: ${a[lvl]}`).join(" · ");
}

/** Gruppen ohne Team-/Abteilungszuordnung bündelt das Backend unter diesem Namen. */
const OHNE_ZUORDNUNG = "Ohne Zuordnung";

// ── Seite ─────────────────────────────────────────────────────────────────────

export function Wirtschaftlichkeit() {
  const [searchParams, setSearchParams] = useSearchParams();
  const team = searchParams.get("team");
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(null);

  // Ebene 1 + 2 teilen sich denselben Endpunkt; ?gruppe= grenzt Kennzahlen und
  // Verlauf auf ein Team ein, die Teamliste bleibt vollständig.
  const steuerungQ = useQuery({
    queryKey: ["wirtschaft-steuerung", team],
    queryFn: () => wirtschaftApi.getSteuerung(team ?? undefined),
    staleTime: 5 * 60_000,
    retry: 1,
  });

  // Ebene 2 braucht zusätzlich die Personenzeilen — clientseitig gefiltert,
  // damit der Wechsel zwischen Teams keinen neuen Request auslöst.
  const teamQ = useQuery({
    queryKey: ["mein-team"],
    queryFn: wirtschaftApi.getTeam,
    staleTime: 5 * 60_000,
    retry: 1,
    enabled: team !== null,
  });

  const steuerung = steuerungQ.data;
  const gesamt = steuerung?.gesamt;

  const isLoading = steuerungQ.isLoading || (team !== null && teamQ.isLoading);
  const isFetching = steuerungQ.isFetching || teamQ.isFetching;
  const error = steuerungQ.error ?? teamQ.error;

  // Nur die Team-Auswahl. Sortiert, gesucht und gefiltert wird in der
  // Tabelle selbst — dort sitzt auch der Zustand der Spaltenkoepfe.
  const mitarbeiter = useMemo(() => {
    if (team === null) return [];
    const rows = teamQ.data?.rows ?? [];
    return rows.filter((r) => (r.team ?? r.department ?? OHNE_ZUORDNUNG) === team);
  }, [team, teamQ.data]);

  const chartData = useMemo(
    () =>
      (steuerung?.verlauf ?? []).map((p) => ({
        label: fmtMonthShort(p.yearMonth),
        umsatz: p.umsatz,
        kosten: p.kosten,
      })),
    [steuerung],
  );

  // Kurzlabel → Langform für den Tooltip (Kurzlabel ist über 13 Monate eindeutig)
  const monatLang = useMemo(
    () =>
      new Map(
        (steuerung?.verlauf ?? []).map((p) => [
          fmtMonthShort(p.yearMonth),
          fmtMonthLong(p.yearMonth),
        ]),
      ),
    [steuerung],
  );

  const deckungsbeitrag = gesamt ? gesamt.umsatz12m - gesamt.kosten12m : null;
  const kostenHinweis = gesamt?.kostenUnvollstaendig
    ? "Für mindestens eine Rolle ist kein Kostensatz hinterlegt — der Wert ist eine Untergrenze."
    : undefined;

  function openTeam(gruppe: string) {
    setSearchParams({ team: gruppe });
  }

  function openGesamt() {
    setSearchParams({});
  }

  return (
    <AppLayout>
      <Header
        title="Wirtschaftlichkeit"
        subtitle={
          team !== null
            ? `${team} — Umsatz, Kosten und Auslastung des Teams`
            : "Gesamt → Team → Mitarbeiter — eine Umsatz-Wahrheit"
        }
      />

      <div className="flex-1 overflow-y-auto flex flex-col px-6 py-5 page-enter">
        {/* Brotkrumen — der Aufriss, nicht drei getrennte Reiter */}
        <nav aria-label="Aufriss" className="flex items-center gap-1.5 mb-4 text-xs">
          {team === null ? (
            <span className="font-semibold" style={{ color: "var(--text-primary)" }}>
              Gesamt
            </span>
          ) : (
            <>
              <button
                type="button"
                onClick={openGesamt}
                className="px-1 py-0.5 rounded transition-colors"
                style={{ color: "var(--accent)", background: "transparent" }}
              >
                Gesamt
              </button>
              <ChevronRight size={12} aria-hidden style={{ color: "var(--text-muted)" }} />
              <span
                aria-current="page"
                className="font-semibold"
                style={{ color: "var(--text-primary)" }}
              >
                {team}
              </span>
            </>
          )}

          <button
            type="button"
            onClick={() => {
              void steuerungQ.refetch();
              if (team !== null) void teamQ.refetch();
            }}
            disabled={isFetching}
            className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded text-xs"
            style={{
              border: "1px solid var(--border)",
              color: "var(--text-muted)",
              background: "transparent",
            }}
          >
            <RefreshCw size={11} className={isFetching ? "animate-spin" : ""} />
            {isFetching ? "Lädt…" : "Aktualisieren"}
          </button>
        </nav>

        {/* Demo-Hinweis (Etappe 0) — Wortlaut identisch zu „Mein Team" */}
        {steuerung?.dataBasis === "demo" && (
          <div className="mb-4">
            <span
              className="chip"
              style={{
                background: "var(--accent-gold-dim)",
                border: "1px solid var(--accent-gold-dim)",
                color: "var(--text-secondary)",
              }}
            >
              <Database size={11} aria-hidden style={{ color: "var(--accent-gold)" }} />
              Demo-Datenbasis (Etappe 0) — echte Salesforce-Zahlen folgen mit dem Datenfundament
            </span>
          </div>
        )}

        {steuerungQ.isError && (
          <div className="card flex items-center justify-center gap-2 px-6 py-8 mb-4">
            <AlertCircle size={15} style={{ color: "var(--risk-high)" }} />
            <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
              {error instanceof Error ? error.message : "Fehler beim Laden der Daten"}
            </p>
          </div>
        )}

        {isLoading && (
          <div className="flex flex-col gap-3" aria-label="Wirtschaftsdaten werden geladen">
            <div className="grid grid-cols-3 gap-3">
              {["k1", "k2", "k3", "k4", "k5", "k6"].map((k) => (
                <div key={k} className="skeleton" style={{ height: 92 }} />
              ))}
            </div>
            <div className="skeleton" style={{ height: 260 }} />
            <div className="skeleton" style={{ height: 220 }} />
          </div>
        )}

        {!isLoading && !steuerungQ.isError && gesamt && steuerung && (
          <>
            {/* ── Kennzahlen ────────────────────────────────────────────── */}
            <div className="stagger grid grid-cols-3 gap-3 mb-3">
              <KpiTile
                hero
                label="Umsatz 12M"
                glossar="gesamtUmsatz12m"
                value={fmtEur.format(gesamt.umsatz12m)}
                sub="Rollendes Jahr, letzte 12 abgeschlossene Monate"
              />
              <KpiTile
                label="Kosten 12M"
                glossar="gesamtKosten12m"
                value={fmtEur.format(gesamt.kosten12m)}
                sub={
                  gesamt.kostenUnvollstaendig
                    ? "Rollen-Vollkosten · unvollständig"
                    : "Rollen-Vollkosten"
                }
                title={kostenHinweis}
              />
              <KpiTile
                label="Deckungsbeitrag / Marge"
                glossar="gesamtDeckungsbeitrag"
                value={
                  deckungsbeitrag == null
                    ? "—"
                    : gesamt.deckungsbeitragPct12m == null
                      ? fmtEur.format(deckungsbeitrag)
                      : `${fmtEur.format(deckungsbeitrag)} · ${gesamt.deckungsbeitragPct12m} %`
                }
                sub="Umsatz − Kosten · Marge = davon ÷ Umsatz"
                valueColor={
                  deckungsbeitrag != null && deckungsbeitrag < 0
                    ? "var(--risk-high-text)"
                    : "var(--text-primary)"
                }
                title={
                  deckungsbeitrag != null && deckungsbeitrag < 0
                    ? "Negativer Deckungsbeitrag: Die Kosten übersteigen den Umsatz."
                    : kostenHinweis
                }
              />
              <KpiTile
                label="Ø Auslastung 3M"
                glossar="gesamtAuslastung3m"
                value={fmtPct(gesamt.utilizationAvg3m)}
                sub="Abgerechnete zu verfügbaren Stunden"
              />
              {/* Bis zum 04.09.2026 stand hier „Ø Marge“ — die gemittelte
                  STUNDENSATZ-Marge. Marie: „Es gibt nur noch die Kachel mit dem
                  DB und der DB Marge.“ Die steht jetzt links im DB-Feld. */}
              <KpiTile
                label="Mitarbeiter"
                glossar="gesamtMitarbeiter"
                value={gesamt.headcount.toLocaleString("de-DE")}
                sub={team !== null ? `im Team ${team}` : "in der Sicht"}
              />
            </div>

            {/* ── Ampelverteilung ───────────────────────────────────────── */}
            <div className="card p-4 mb-3">
              <div className="flex items-baseline justify-between mb-2">
                <p
                  className="text-[9px] font-bold tracking-widest uppercase"
                  style={{ color: "var(--text-muted)" }}
                >
                  Ampelverteilung
                </p>
                <p className="text-[10px]" style={{ color: "var(--text-muted)" }}>
                  Wirtschafts-Ampel je Mitarbeiter
                </p>
              </div>
              <AmpelBar ampeln={gesamt.ampeln} />
            </div>

            {/* ── Monatsverlauf (früher „Projektumsatz") ────────────────── */}
            <div className="card p-4 mb-3">
              <div className="flex items-baseline justify-between mb-3">
                <p
                  className="text-[9px] font-bold tracking-widest uppercase"
                  style={{ color: "var(--text-muted)" }}
                >
                  Monatsverlauf
                </p>
                <p className="text-[10px]" style={{ color: "var(--text-muted)" }}>
                  Umsatz und Kosten je Monat
                </p>
              </div>
              {chartData.length === 0 ? (
                <p className="text-sm py-8 text-center" style={{ color: "var(--text-muted)" }}>
                  Kein Monatsverlauf vorhanden
                </p>
              ) : (
                <ResponsiveContainer width="100%" height={230}>
                  <ComposedChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid
                      strokeDasharray="2 4"
                      stroke="var(--chart-grid)"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="label"
                      tickLine={false}
                      axisLine={false}
                      tick={{ fontSize: 10, fill: "var(--text-muted)" as string }}
                    />
                    {/* Gemeinsame €-Achse für Umsatz und Kosten — sonst wären die
                        beiden Serien optisch nicht vergleichbar. */}
                    <YAxis
                      tickLine={false}
                      axisLine={false}
                      width={46}
                      tickFormatter={fmtEurAxis}
                      tick={{ fontSize: 10, fill: "var(--text-muted)" as string }}
                    />
                    <Tooltip
                      contentStyle={{
                        background: "var(--bg-card)",
                        border: "1px solid var(--border)",
                        borderRadius: 8,
                        fontSize: 11,
                        color: "var(--text-primary)",
                      }}
                      formatter={(v: number, name: string) => [fmtEur.format(v), name]}
                      labelFormatter={(l: string) => monatLang.get(l) ?? l}
                    />
                    <Legend
                      wrapperStyle={{ fontSize: 11, color: "var(--text-muted)" }}
                      iconSize={9}
                    />
                    {/* Serie 1 = --chart-1, Kostenlinie = --chart-reference gestrichelt */}
                    <Bar
                      dataKey="umsatz"
                      name="Umsatz"
                      fill="var(--chart-1)"
                      radius={[4, 4, 0, 0]}
                      maxBarSize={30}
                    />
                    <Line
                      type="monotone"
                      dataKey="kosten"
                      name="Kosten"
                      stroke="var(--chart-reference)"
                      strokeWidth={2}
                      strokeDasharray="4 3"
                      dot={false}
                      activeDot={{ r: 3.5 }}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              )}
              {gesamt.kostenUnvollstaendig && (
                <p className="text-[10px] mt-1.5" style={{ color: "var(--text-muted)" }}>
                  Die Kostenlinie enthält nur Rollen mit hinterlegtem Kostensatz.
                </p>
              )}
            </div>

            {/* ── Ebene 1: Teams · Ebene 2: Mitarbeiter ─────────────────── */}
            {team === null ? (
              <TeamTabelle gruppen={steuerung.gruppen} onOpen={openTeam} />
            ) : (
              <MitarbeiterListe
                team={team}
                rows={mitarbeiter}
                isError={teamQ.isError}
                onOpen={setSelectedEmployeeId}
              />
            )}

            <p className="mt-3 text-xs" style={{ color: "var(--text-muted)" }}>
              Stand: letzter abgeschlossener Monat {fmtMonthLong(steuerung.lastClosedMonth)}
            </p>
          </>
        )}
      </div>

      {/*
        key={selectedEmployeeId} ist Pflicht (wie in MeinTeam, RetentionRadar und
        Cockpit): Der Drawer rendert bei employeeId === null nur `null`, wird aber
        nie unmountet — ohne key überlebt sein lokaler State den Personenwechsel
        und ein offenes, ungespeichertes Maßnahmen-Formular über Person A ließe
        sich auf Person B speichern.
      */}
      <EmployeeDetailDrawer
        key={selectedEmployeeId}
        employeeId={selectedEmployeeId}
        onClose={() => setSelectedEmployeeId(null)}
      />
    </AppLayout>
  );
}

// ── Bausteine ─────────────────────────────────────────────────────────────────

function KpiTile({
  label,
  value,
  sub,
  hero,
  valueColor,
  title,
  glossar,
}: {
  label: string;
  value: string;
  sub: string;
  hero?: boolean | undefined;
  valueColor?: string | undefined;
  title?: string | undefined;
  /** Schluessel in `lib/glossar.ts`; ohne ihn bleibt die Kachel ohne Info-Symbol. */
  glossar?: string | undefined;
}) {
  return (
    <div className="card card-hover p-4 relative" title={title}>
      <div className="flex items-center justify-between gap-1 mb-1.5">
        <p
          className="text-[9px] font-bold tracking-widest uppercase leading-tight"
          style={{ color: "var(--text-muted)" }}
        >
          {label}
        </p>
        {glossar && <KpiInfo schluessel={glossar} label={label} />}
      </div>
      <div
        className={`font-bold font-mono-data tabular-nums leading-none ${hero ? "text-3xl" : "text-2xl"}`}
        style={{ color: valueColor ?? (hero ? "var(--accent)" : "var(--text-primary)") }}
      >
        {value}
      </div>
      <p className="text-[10px] mt-1.5" style={{ color: "var(--text-muted)" }}>
        {sub}
      </p>
    </div>
  );
}

/** Segmentierter Balken + Zahlen-Legende — Farbe steht nie allein. */
function AmpelBar({ ampeln }: { ampeln: Ampeln }) {
  const summe = ampeln.ok + ampeln.warning + ampeln.critical;
  return (
    <>
      {summe > 0 ? (
        <div className="flex" style={{ gap: 2, height: 10 }} title={ampelnTitle(ampeln)}>
          {AMPEL_ORDER.filter((lvl) => ampeln[lvl] > 0).map((lvl) => (
            <div
              key={lvl}
              title={`${LEVEL_LABEL[lvl]}: ${ampeln[lvl]}`}
              style={{
                flexGrow: ampeln[lvl],
                flexBasis: 0,
                borderRadius: "var(--radius-pill)",
                background: LEVEL_COLOR[lvl],
              }}
            />
          ))}
        </div>
      ) : (
        <div
          style={{ height: 10, borderRadius: "var(--radius-pill)", background: "var(--bg-inset)" }}
        />
      )}
      <div
        className="flex items-center gap-4 mt-2 text-[11px]"
        style={{ color: "var(--text-secondary)" }}
      >
        {AMPEL_ORDER.map((lvl) => (
          <span key={lvl} className="inline-flex items-center gap-1.5 whitespace-nowrap">
            <span
              className="inline-block w-2 h-2 rounded-full"
              style={{ background: LEVEL_COLOR[lvl] }}
            />
            <span className="font-mono-data tabular-nums">{ampeln[lvl]}</span>
            {LEVEL_LABEL[lvl]}
          </span>
        ))}
      </div>
    </>
  );
}

/** Kompakte Ampel-Zahlen für eine Tabellenzeile. */
function AmpelZahlen({ ampeln }: { ampeln: Ampeln }) {
  return (
    <span className="flex items-center justify-end gap-2.5" title={ampelnTitle(ampeln)}>
      {AMPEL_ORDER.map((lvl) => (
        <span
          key={lvl}
          className="inline-flex items-center gap-1 text-[11px]"
          style={{ color: "var(--text-secondary)" }}
        >
          <span
            className="inline-block w-1.5 h-1.5 rounded-full"
            style={{ background: LEVEL_COLOR[lvl] }}
          />
          <span className="font-mono-data tabular-nums">{ampeln[lvl]}</span>
        </span>
      ))}
    </span>
  );
}

// Standardreihenfolge der Teamtabelle: umsatzstaerkstes Team oben — so
// liefert sie der Server (aggregateGruppen). Der dritte Klick auf einen
// Spaltenkopf fuehrt hierher zurueck.
const STANDARD_TEAM = (a: WirtschaftGruppe, b: WirtschaftGruppe) => b.umsatz12m - a.umsatz12m;

const SORT_TEAM: SortSpalten<WirtschaftGruppe> = {
  gruppe: { wert: (g) => g.gruppe, typ: "text" },
  headcount: { wert: (g) => g.headcount, typ: "zahl" },
  umsatz: { wert: (g) => g.umsatz12m, typ: "zahl" },
  kosten: { wert: (g) => g.kosten12m, typ: "zahl" },
  db: { wert: (g) => g.umsatz12m - g.kosten12m, typ: "zahl" },
  auslastung: { wert: (g) => g.utilizationAvg3m, typ: "zahl" },
  marge: { wert: (g) => g.deckungsbeitragPct12m, typ: "zahl" },
  // Nach Ampeln heisst: wo brennt es am meisten. Rot wiegt schwerer als Gelb,
  // deshalb ein gewichteter Wert statt der blossen Summe.
  ampeln: { wert: (g) => g.ampeln.critical * 1000 + g.ampeln.warning, typ: "zahl" },
};

const GRID_TEAM = "minmax(150px, 1.4fr) 80px 120px 120px 130px 100px 90px 130px";
const GRID_EMP = "minmax(180px, 2fr) 130px 130px 100px 130px";

function TeamTabelle({
  gruppen,
  onOpen,
}: {
  gruppen: WirtschaftGruppe[];
  onOpen: (gruppe: string) => void;
}) {
  const { klick, sortiere, richtungVon } = useTabellenSortierung(SORT_TEAM, STANDARD_TEAM);
  const zeilen = useMemo(() => sortiere(gruppen), [sortiere, gruppen]);

  // `shrink-0` ist Pflicht, nicht Kosmetik. Ohne das druecken die Flexbox-
  // Geschwister (Kacheln, Monatsverlauf) diese Karte zusammen, und ihr
  // `overflow-hidden` schneidet den Rest ab, statt zu scrollen.
  //
  // Gemessen am 09.09.2026 im Team "CU - Talentnetzwerk": Karte 158 px hoch,
  // Inhalt 9.495 px — von 163 Zeilen waren etwa ZWEI sichtbar, und weil die
  // Karte schrumpfte, hatte auch die SEITE nichts zu scrollen. Armin Kern
  // (Bereichsleiter): "fuer TL mit <= 7 MA kein Problem, fuer mich als BL so
  // nicht nutzbar" — bei wenigen Zeilen passte es zufaellig hinein.
  //
  // Mit `shrink-0` behaelt die Karte ihre volle Hoehe, und der Seiten-
  // Container (`overflow-y-auto`) scrollt wie vorgesehen.
  return (
    <div className="card overflow-hidden shrink-0">
      <div
        className="grid text-xs font-medium px-4 py-3"
        style={{
          gridTemplateColumns: GRID_TEAM,
          gap: "0 12px",
          color: "var(--text-muted)",
          borderBottom: "1px solid var(--border)",
        }}
      >
        <SortKopf
          label="Team"
          spalte="gruppe"
          zusatz="↗ Aufriss"
          richtung={richtungVon("gruppe")}
          onKlick={klick}
        />
        <SortKopf
          label="Mitarbeiter"
          spalte="headcount"
          info="tabelleTeamMitarbeiter"
          rechts
          richtung={richtungVon("headcount")}
          onKlick={klick}
        />
        <SortKopf
          label="Umsatz 12M"
          spalte="umsatz"
          info="tabelleTeamUmsatz12m"
          rechts
          richtung={richtungVon("umsatz")}
          onKlick={klick}
        />
        <SortKopf
          label="Kosten 12M"
          spalte="kosten"
          info="tabelleTeamKosten12m"
          rechts
          richtung={richtungVon("kosten")}
          onKlick={klick}
        />
        <SortKopf
          label="Deckungsbeitrag"
          spalte="db"
          info="tabelleTeamDeckungsbeitrag"
          rechts
          richtung={richtungVon("db")}
          onKlick={klick}
        />
        <SortKopf
          label="Ø Auslastung"
          spalte="auslastung"
          info="tabelleTeamAuslastung3m"
          rechts
          richtung={richtungVon("auslastung")}
          onKlick={klick}
        />
        <SortKopf
          label="Marge"
          spalte="marge"
          info="tabelleTeamMarge"
          rechts
          richtung={richtungVon("marge")}
          onKlick={klick}
        />
        <SortKopf
          label="Ampeln"
          spalte="ampeln"
          info="tabelleTeamAmpeln"
          rechts
          richtung={richtungVon("ampeln")}
          onKlick={klick}
        />
      </div>

      {zeilen.length === 0 && (
        <div
          className="flex items-center justify-center h-28 text-sm"
          style={{ color: "var(--text-muted)" }}
        >
          Keine Teams in dieser Sicht
        </div>
      )}

      {zeilen.map((g) => {
        const db = g.umsatz12m - g.kosten12m;
        return (
          <button
            key={g.gruppe}
            type="button"
            onClick={() => onOpen(g.gruppe)}
            className="w-full grid items-center px-4 py-3 row-hover text-left"
            style={{
              gridTemplateColumns: GRID_TEAM,
              gap: "0 12px",
              borderBottom: "1px solid var(--border)",
              background: "transparent",
            }}
          >
            <span className="flex items-center gap-1.5 min-w-0">
              <span
                className="text-xs font-medium truncate"
                style={{ color: "var(--text-primary)" }}
              >
                {g.gruppe}
              </span>
              <ChevronRight
                size={12}
                aria-hidden
                className="shrink-0"
                style={{ color: "var(--text-muted)" }}
              />
            </span>
            <span
              className="font-mono-data text-xs text-right tabular-nums"
              style={{ color: "var(--text-secondary)" }}
            >
              {g.headcount}
            </span>
            <span
              className="font-mono-data text-xs text-right tabular-nums"
              style={{ color: "var(--text-primary)" }}
            >
              {fmtEur.format(g.umsatz12m)}
            </span>
            <span
              className="font-mono-data text-xs text-right tabular-nums"
              title={
                g.kostenUnvollstaendig
                  ? "Für mindestens eine Rolle ist kein Kostensatz hinterlegt — der Wert ist eine Untergrenze."
                  : undefined
              }
              style={{ color: "var(--text-secondary)" }}
            >
              {fmtEur.format(g.kosten12m)}
              {g.kostenUnvollstaendig && (
                <TriangleAlert
                  size={10}
                  aria-hidden
                  className="inline ml-1 align-[-1px]"
                  style={{ color: "var(--risk-medium)" }}
                />
              )}
            </span>
            <span
              className="font-mono-data text-xs text-right tabular-nums font-semibold"
              title={db < 0 ? "Negativer Deckungsbeitrag" : undefined}
              style={{ color: db < 0 ? "var(--risk-high-text)" : "var(--text-primary)" }}
            >
              {fmtEur.format(db)}
            </span>
            <span
              className="font-mono-data text-xs text-right tabular-nums"
              style={{
                color: g.utilizationAvg3m == null ? "var(--text-muted)" : "var(--text-primary)",
              }}
            >
              {fmtPct(g.utilizationAvg3m)}
            </span>
            <span
              className="font-mono-data text-xs text-right tabular-nums"
              title={
                g.deckungsbeitragPct12m == null
                  ? "Keine Marge berechenbar — kein Umsatz im Zeitraum"
                  : undefined
              }
              style={{
                color:
                  g.deckungsbeitragPct12m == null
                    ? "var(--text-muted)"
                    : g.deckungsbeitragPct12m < 0
                      ? "var(--risk-high-text)"
                      : "var(--text-primary)",
              }}
            >
              {fmtPct(g.deckungsbeitragPct12m)}
            </span>
            <AmpelZahlen ampeln={g.ampeln} />
          </button>
        );
      })}
    </div>
  );
}

// Standardreihenfolge: kritischste Wirtschafts-Ampel oben, darin der groesste
// Umsatz. Dieselbe Regel wie "Mein Team" in der Wirtschafts-Ansicht
// (lib/team-reihenfolge.ts, Entscheidung Marie 10.09.2026).
const STANDARD_MITARBEITER = nachWirtschaftsAmpel;

const SORT_EMP: SortSpalten<MeinTeamRow> = {
  name: { wert: (r) => r.name, typ: "text" },
  auslastung: { wert: (r) => r.wirtschaft.utilizationAvg3m, typ: "zahl" },
  umsatz: { wert: (r) => r.wirtschaft.umsatz12m, typ: "zahl" },
  marge: { wert: (r) => r.wirtschaft.deckungsbeitragPct12m, typ: "zahl" },
  ampel: { wert: (r) => LEVEL_RANK[r.wirtschaft.level], typ: "zahl" },
};

function MitarbeiterListe({
  team,
  rows,
  isError,
  onOpen,
}: {
  team: string;
  rows: MeinTeamRow[];
  isError: boolean;
  onOpen: (id: number) => void;
}) {
  const [suche, setSuche] = useState("");
  const [fk, setFk] = useState("");
  const { klick, sortiere, richtungVon } = useTabellenSortierung(SORT_EMP, STANDARD_MITARBEITER);

  // Auswahlliste der Fuehrungskraefte — nur die, die in DIESEM Team vorkommen.
  // Gemessen am 09.09.2026: 29 von 33 Teams haben mehr als eine Fuehrungskraft
  // ("CU - ITIT - Projektumsetzung" sogar 13), deshalb trennt der Filter hier
  // wirklich. Bei den restlichen vier bleibt er stumm (siehe unten).
  const fkListe = useMemo(() => {
    const namen = new Set<string>();
    for (const r of rows) if (r.fuehrungskraft) namen.add(r.fuehrungskraft);
    return [...namen].sort((a, b) => a.localeCompare(b, "de"));
  }, [rows]);

  const zeilen = useMemo(() => {
    const q = suche.trim().toLowerCase();
    const gefiltert = rows.filter((r) => {
      if (fk !== "" && r.fuehrungskraft !== fk) return false;
      if (q === "") return true;
      // Position mitsuchen: "Senior Consultant" findet die ganze Stufe.
      return r.name.toLowerCase().includes(q) || (r.position?.toLowerCase().includes(q) ?? false);
    });
    return sortiere(gefiltert);
  }, [rows, suche, fk, sortiere]);

  const eingegrenzt = suche.trim() !== "" || fk !== "";

  // `shrink-0` ist Pflicht, nicht Kosmetik. Ohne das druecken die Flexbox-
  // Geschwister (Kacheln, Monatsverlauf) diese Karte zusammen, und ihr
  // `overflow-hidden` schneidet den Rest ab, statt zu scrollen.
  //
  // Gemessen am 09.09.2026 im Team "CU - Talentnetzwerk": Karte 158 px hoch,
  // Inhalt 9.495 px — von 163 Zeilen waren etwa ZWEI sichtbar, und weil die
  // Karte schrumpfte, hatte auch die SEITE nichts zu scrollen. Armin Kern
  // (Bereichsleiter): "fuer TL mit <= 7 MA kein Problem, fuer mich als BL so
  // nicht nutzbar" — bei wenigen Zeilen passte es zufaellig hinein.
  //
  // Mit `shrink-0` behaelt die Karte ihre volle Hoehe, und der Seiten-
  // Container (`overflow-y-auto`) scrollt wie vorgesehen.
  return (
    <>
      {/* Suche + Fuehrungskraft-Filter (Marie, 09.09.2026) */}
      <div className="flex items-center gap-3 mb-3 shrink-0">
        <div
          className="flex items-center gap-2 px-3 py-2 rounded flex-1 max-w-xs"
          style={{ background: "var(--bg-card)", border: "1px solid var(--border)" }}
        >
          <Search size={13} aria-hidden style={{ color: "var(--text-muted)" }} />
          <input
            value={suche}
            onChange={(e) => setSuche(e.target.value)}
            placeholder="Name oder Position suchen…"
            aria-label="Mitarbeiter suchen"
            className="bg-transparent text-sm outline-none w-full"
            style={{ color: "var(--text-primary)" }}
          />
        </div>

        {/* Nur zeigen, wenn es im Team ueberhaupt etwas zu unterscheiden gibt.
            Ein Auswahlfeld mit einem einzigen Eintrag waere nur Ballast. */}
        {fkListe.length > 1 && (
          <select
            value={fk}
            onChange={(e) => setFk(e.target.value)}
            aria-label="Nach Führungskraft filtern"
            className="text-sm rounded px-3 py-2 outline-none shrink-0"
            style={{
              background: "var(--bg-card)",
              border: `1px solid ${fk === "" ? "var(--border)" : "var(--accent-gold)"}`,
              color: fk === "" ? "var(--text-muted)" : "var(--text-primary)",
            }}
          >
            <option value="">Alle Führungskräfte</option>
            {fkListe.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        )}

        {eingegrenzt && (
          <button
            type="button"
            onClick={() => {
              setSuche("");
              setFk("");
            }}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs shrink-0"
            style={{
              border: "1px solid var(--border)",
              color: "var(--text-muted)",
              background: "transparent",
            }}
          >
            <X size={11} aria-hidden />
            Filter zurücksetzen
          </button>
        )}

        <span className="ml-auto text-xs shrink-0" style={{ color: "var(--text-muted)" }}>
          {eingegrenzt ? `${zeilen.length} von ${rows.length}` : `${rows.length}`} Mitarbeiter
        </span>
      </div>

      <div className="card overflow-hidden shrink-0">
        <div
          className="grid text-xs font-medium px-4 py-3"
          style={{
            gridTemplateColumns: GRID_EMP,
            gap: "0 12px",
            color: "var(--text-muted)",
            borderBottom: "1px solid var(--border)",
          }}
        >
          <SortKopf
            label="Mitarbeiter"
            spalte="name"
            zusatz="↗ Detail"
            richtung={richtungVon("name")}
            onKlick={klick}
          />
          <SortKopf
            label="Auslastung Ø 3M"
            spalte="auslastung"
            info="utilizationAvg3m"
            rechts
            richtung={richtungVon("auslastung")}
            onKlick={klick}
          />
          <SortKopf
            label="Umsatz 12M"
            spalte="umsatz"
            info="umsatz12m"
            rechts
            richtung={richtungVon("umsatz")}
            onKlick={klick}
          />
          <SortKopf
            label="Marge"
            spalte="marge"
            info="tabelleMitarbeiterMarge"
            rechts
            richtung={richtungVon("marge")}
            onKlick={klick}
          />
          <SortKopf
            label="Wirtschafts-Ampel"
            spalte="ampel"
            info="tabelleMitarbeiterAmpel"
            rechts
            richtung={richtungVon("ampel")}
            onKlick={klick}
          />
        </div>

        {isError && (
          <div className="flex items-center justify-center h-28 gap-2 px-6">
            <AlertCircle size={15} style={{ color: "var(--risk-high)" }} />
            <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
              Mitarbeiterliste konnte nicht geladen werden
            </p>
          </div>
        )}

        {/* Zwei verschiedene Nullfaelle — "hier ist niemand" und "der Filter
          passt auf niemanden" brauchen verschiedene Antworten. */}
        {!isError && zeilen.length === 0 && (
          <div
            className="flex items-center justify-center h-28 text-sm"
            style={{ color: "var(--text-muted)" }}
          >
            {rows.length === 0
              ? `Keine Mitarbeiter in ${team} sichtbar`
              : "Kein Mitarbeiter passt zu Suche und Filter"}
          </div>
        )}

        {zeilen.map((row) => (
          <button
            key={row.id}
            type="button"
            onClick={() => onOpen(row.id)}
            className="w-full grid items-center px-4 py-3 row-hover text-left"
            style={{
              gridTemplateColumns: GRID_EMP,
              gap: "0 12px",
              borderBottom: "1px solid var(--border)",
              background: "transparent",
            }}
          >
            <span className="min-w-0 block">
              <span
                className="block text-xs font-medium truncate"
                style={{ color: "var(--text-primary)" }}
              >
                {row.name}
              </span>
              {row.position && (
                <span
                  className="block text-[10px] mt-0.5 truncate"
                  style={{ color: "var(--text-muted)" }}
                >
                  {row.position}
                </span>
              )}
            </span>
            <span
              className="font-mono-data text-xs text-right tabular-nums"
              style={{
                color:
                  row.wirtschaft.utilizationAvg3m == null
                    ? "var(--text-muted)"
                    : "var(--text-primary)",
              }}
            >
              {fmtPct(row.wirtschaft.utilizationAvg3m)}
            </span>
            <span
              className="font-mono-data text-xs text-right tabular-nums"
              style={{ color: "var(--text-primary)" }}
            >
              {fmtEur.format(row.wirtschaft.umsatz12m)}
            </span>
            <span
              className="font-mono-data text-xs text-right tabular-nums"
              title={
                row.wirtschaft.deckungsbeitragPct12m == null
                  ? "Keine Marge berechenbar — kein Umsatz im Zeitraum"
                  : undefined
              }
              style={{
                color:
                  row.wirtschaft.deckungsbeitragPct12m == null
                    ? "var(--text-muted)"
                    : row.wirtschaft.deckungsbeitragPct12m < 0
                      ? "var(--risk-high-text)"
                      : "var(--text-primary)",
              }}
            >
              {fmtPct(row.wirtschaft.deckungsbeitragPct12m)}
            </span>
            {/* Farbe nie allein: Punkt + Klartext-Label, Tooltip mit den Alerts */}
            <span
              className="flex items-center justify-end gap-1.5 text-[11px]"
              title={wirtschaftTitle(row)}
              style={{ color: LEVEL_TEXT_COLOR[row.wirtschaft.level] }}
            >
              <span
                className="inline-block w-2 h-2 rounded-full shrink-0"
                style={{ background: LEVEL_COLOR[row.wirtschaft.level] }}
              />
              {LEVEL_LABEL[row.wirtschaft.level]}
              {row.wirtschaft.level === "critical" && (
                <TriangleAlert size={11} aria-hidden style={{ color: "var(--risk-high)" }} />
              )}
            </span>
          </button>
        ))}
      </div>
    </>
  );
}

function wirtschaftTitle(row: MeinTeamRow): string {
  const head = `Wirtschaftlichkeit: ${LEVEL_LABEL[row.wirtschaft.level]}`;
  if (row.wirtschaft.alerts.length === 0) return head;
  return `${head}\n${row.wirtschaft.alerts.map((a) => `• ${a.message}`).join("\n")}`;
}
